import React from 'react'
import styles from './UIGamePlayerInformationProgress.module.scss'
import clsx from 'clsx'
import {T, Tooltip} from '@colonist/ui'
import {type GameModeTranslationKeyContext} from '@colonist/i18n'
import {UIGameCityImprovementTracker} from '../../../common/UIGameCityImprovementTracker/UIGameCityImprovementTracker.tsx'
import {type MechanicCityImprovementsPlayerState, type PlayerState, VictoryPointType} from '@colonist/game-states'
import {VictoryPointStateReader} from '@colonist/game-base-types'
import IconDefender from '@static/images/icon_defender.svg'
import IconTrophy from '@static/images/icon_trophy.svg'
import IconArrowPlayerProfile from '@static/images/icon_arrow_player_profile.svg'
import IconLargestArmy from '@static/images/icon_largest_army.svg'
import IconLongestRoad from '@static/images/icon_longest_road.svg'
import IconLargestArmyHighlight from '@static/images/icon_largest_army_highlight.svg'
import IconLongestRoadHighlight from '@static/images/icon_longest_road_highlight.svg'
import IconSwords from '@static/images/icon_swords.svg'
import IconSwordsHighlight from '@static/images/icon_swords_highlight.svg'
import {type PlayerValidator} from '@colonist/game-mechanic-validators'

const CityImprovementTracker = ({
    cityImprovementState,
}: {
    cityImprovementState: MechanicCityImprovementsPlayerState
}) => (
    <Tooltip
        className={styles.cityImprovements}
        content={<div className={styles.cityImprovementTooltip}>
            <div><T id="strings:game.tips.cityImprovementButton.body.0" /></div>
            <div><T id="strings:game.tips.cityImprovementButton.body.1" /></div>
        </div>}
        menuClassName={styles.tooltipMenu}
        title={<T id="strings:game.tips.cityImprovementButton.title" />}
    >
        <UIGameCityImprovementTracker
            cityImprovementState={cityImprovementState}
            className={styles.cityImprovementTracker}
        />
    </Tooltip>
)

const LargestArmy = ({
    knightDevelopmentCardsUsedCount,
    hasLargestArmy,
}: {
    knightDevelopmentCardsUsedCount: number
    hasLargestArmy: boolean
}) => (
    <Tooltip
        className={styles.achievementItem}
        content={<T id="strings:game.playerInformation.largestArmy.body" />}
        menuClassName={styles.tooltipMenu}
        title={<T id="strings:game.playerInformation.largestArmy.title" />}
    >
        <img
            className={styles.icon}
            src={hasLargestArmy ? IconLargestArmyHighlight : IconLargestArmy}
        />
        <div className={styles.achievementCount}>{knightDevelopmentCardsUsedCount}</div>
    </Tooltip>
)

const LongestRoad = ({
    isCityAndKnightMode,
    longestRoadCount,
    hasLongestRoad,
    translationKeyContext,
}: {
    isCityAndKnightMode: boolean
    longestRoadCount: number
    hasLongestRoad: boolean
    translationKeyContext: GameModeTranslationKeyContext
}) => (
    <Tooltip
        className={clsx(styles.achievementItem, {
            [styles.horizontal]: isCityAndKnightMode,
        })}
        content={<T id={{
            key: 'strings:game.playerInformation.longestRoad.body',
            options: {context: translationKeyContext},
        }} />}
        menuClassName={styles.tooltipMenu}
        title={<T id={{
            key: 'strings:game.playerInformation.longestRoad.title',
            options: {context: translationKeyContext},
        }} />}>
        <img
            className={styles.icon}
            src={hasLongestRoad ? IconLongestRoadHighlight : IconLongestRoad}
        />
        <div className={styles.achievementCount}>{longestRoadCount}</div>
    </Tooltip>
)

const ActiveKnights = ({
    knightStrength,
    hasHighestKnightStrength,
}: {
    knightStrength: number
    hasHighestKnightStrength: boolean
}) => (
    <Tooltip
        className={clsx(styles.achievementItem, styles.horizontal)}
        content={<T id='strings:game.playerInformation.knightStrength.body' />}
        menuClassName={styles.tooltipMenu}
        title={<T id='strings:game.playerInformation.knightStrength.title' />}
    >
        <img
            className={styles.icon}
            src={hasHighestKnightStrength ? IconSwordsHighlight : IconSwords}
        />
        <div className={styles.achievementCount}>{knightStrength}</div>
    </Tooltip>
)

const DefenderOfColonist = ({
    playerState,
}: {
    playerState: PlayerState
}) => {
    const victoryPointsFromDefenderOfColonist = VictoryPointStateReader.totalVictoryPointsOfType(playerState.victoryPointsState,
        [VictoryPointType.DefenderOfColonist])
    return (
        <Tooltip
            className={clsx(styles.achievementItem, styles.horizontal)}
            content={<T id='strings:game.playerInformation.defenderOfColonist.body' />}
            menuClassName={styles.tooltipMenu}
            title={<T id='strings:game.playerInformation.defenderOfColonist.title' />}
        >
            <img
                className={styles.icon}
                src={IconDefender}
            />
            <div className={styles.achievementCount}>{victoryPointsFromDefenderOfColonist}</div>
        </Tooltip>
    )
}

const ProgressCardVictoryPoint = ({
    playerState,
}: {
    playerState: PlayerState
}) => {
    const victoryPointsFromProgressCard = VictoryPointStateReader.totalVictoryPointsOfType(playerState.victoryPointsState,
        [VictoryPointType.ProgressCardVictoryPoint])
    return (
        <Tooltip
            className={clsx(styles.achievementItem, styles.horizontal)}
            content={<T id='strings:game.playerInformation.progressCardVictoryPoint.body' />}
            menuClassName={styles.tooltipMenu}
            title={<T id='strings:game.playerInformation.progressCardVictoryPoint.title' />}
        >
            <img
                className={styles.icon}
                src={IconTrophy}
            />
            <div className={styles.achievementCount}>{victoryPointsFromProgressCard}</div>
        </Tooltip>
    )
}
export interface UIGamePlayerInformationProgressProps {
    isCityAndKnightMode: boolean
    translationKeyContext: GameModeTranslationKeyContext
    isCurrentUser: boolean
    knightDevelopmentCardsUsedCount: number
    knightStrength: number
    hasLargestArmy: boolean
    longestRoadCount: number
    hasLongestRoad: boolean
    hasHighestKnightStrength: boolean
    cityImprovementState: MechanicCityImprovementsPlayerState
    playerValidator: PlayerValidator
}

export const UIGamePlayerInformationProgress = ({
    isCityAndKnightMode,
    cityImprovementState,
    translationKeyContext,
    isCurrentUser,
    knightDevelopmentCardsUsedCount,
    knightStrength,
    hasLargestArmy,
    longestRoadCount,
    hasLongestRoad,
    hasHighestKnightStrength,
    playerValidator,
}: UIGamePlayerInformationProgressProps) => (
    <div className={clsx(styles.container, {
        [styles.currentUser]: isCurrentUser,
    })}>
        {isCityAndKnightMode && <img
            className={styles.viewSwitch}
            src={IconArrowPlayerProfile}
        />}

        {isCityAndKnightMode && (
            <CityImprovementTracker cityImprovementState={cityImprovementState} />
        )}

        {!isCityAndKnightMode && (
            <>
                <LargestArmy
                    hasLargestArmy={hasLargestArmy}
                    knightDevelopmentCardsUsedCount={knightDevelopmentCardsUsedCount}
                />
                <LongestRoad
                    hasLongestRoad={hasLongestRoad}
                    isCityAndKnightMode={isCityAndKnightMode}
                    longestRoadCount={longestRoadCount}
                    translationKeyContext={translationKeyContext}
                />
            </>

        )}

        {isCityAndKnightMode && (
            <>
                <div className={clsx(styles.horizontalAchievements, styles.view1)}>
                    <LongestRoad
                        hasLongestRoad={hasLongestRoad}
                        isCityAndKnightMode={isCityAndKnightMode}
                        longestRoadCount={longestRoadCount}
                        translationKeyContext={translationKeyContext}
                    />
                    <ActiveKnights
                        hasHighestKnightStrength={hasHighestKnightStrength}
                        knightStrength={knightStrength}
                    />
                </div>
                <div className={clsx(styles.horizontalAchievements, styles.view2)}>
                    <ProgressCardVictoryPoint
                        playerState={playerValidator.state}
                    />
                    <DefenderOfColonist
                        playerState={playerValidator.state}
                    />
                </div>
            </>
        )}

    </div>
)
